import { snakeCase, groupBy } from 'lodash';
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ResponsiveLine, PointTooltipProps, Datum } from '@nivo/line';
import { Mesh } from '@nivo/voronoi';
import { useTooltip, Crosshair, useTooltipState } from '@nivo/tooltip';
import { DatumValue } from '@nivo/core';
import { Query } from '@sierralabs/query-core';
import { useRunQuery } from '@sierralabs/query-ui';
import { categoricalColorSchemes } from '@nivo/colors';
import { parseTaskStatus } from '@sierralabs/workflow-core';

import {
  useSubscribeComponentChanges,
  useSiteContext,
  SiteComponent,
  SiteComponentConfigProps,
  SiteComponentPlugin,
  SiteComponentProps,
  SiteComponentStackConfigurator,
  ComponentRenderer,
  ChartItemsEvent,
  ChartItemsEventProps,
  ChartType,
  SiteConfigChart,
  ChartLegends,
  ChartLegendStat
} from '@sierralabs/site-ui';
import { CoreEvent } from '@sierralabs/core-ui';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  isValid,
  addQuarters,
  addDays,
  addMonths,
  addYears
} from 'date-fns';
import styled from 'styled-components';
import { Typography } from '@material-ui/core';
import { ColumnType } from '@sierralabs/entity-core';
import {
  DatasetType,
  DateFormat,
  LineChartQueryBuilderParams,
  LineChartQueryBuilderPlugin
} from '../queries/plugins/LineChartQueryBuilderPlugin';

const DefaultChartHeight = 400;

const StyledTooltip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 5px;
  background: #fff;
  min-height: 20px;
  max-height: 200px;
  min-width: 40px;
  max-width: 400px;
  padding: 4px 8px;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.08);

  div {
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;

    .box {
      width: 20px;
      height: 20px;
    }
  }
`;

const ChartContaner = styled.div<{ height?: number; dataLength?: number }>`
  overflow-y: hidden;
  overflow-x: ${({ dataLength }) =>
    dataLength && dataLength > 10 ? 'scroll' : 'hidden'};
  position: relative;
  height: ${({ height }) => height ?? DefaultChartHeight}px;

  & > div {
    width: ${({ dataLength }) =>
      dataLength && dataLength > 10
        ? `${dataLength * 200}px`
        : '100%'} !important;
  }
`;

interface LineChartQueryData {
  total: number;
  [key: string]: any;
}

interface ChartData {
  id: string;
  uniqueId: string;
  color: string;
  data: {
    x: number | string;
    y: string | number;
    datasetName: string;
    nonFormatted: string | Date | number;
  }[];
}

export interface SiteLineChartProps {
  query?: Partial<Query>;
}

const MeshAndCrosshair = ({
  layerData,
  height
}: {
  layerData: Datum;
  height?: number;
}) => {
  const { showTooltipAt, hideTooltip } = useTooltip();
  const { isVisible } = useTooltipState();
  const x = useRef<DatumValue | null>();
  const y = useRef<DatumValue | null>();

  const handleMouseEnter = (point: Datum) => {
    showTooltipAt(
      layerData.tooltip({ point }),
      [point.x + layerData.margin.left, point.y + layerData.margin.top],
      'top'
    );
  };
  const handleMouseMove = (point: Datum) => {
    showTooltipAt(
      layerData.tooltip({ point }),
      [
        point.x === layerData.innerWidth
          ? (point.x as number)
          : point.x === 0
          ? (point.x as number) + layerData.margin.left * 2
          : point.x + layerData.margin.left,
        (point.y as number) <= 100
          ? (point.y as number) + layerData.margin.top * 2 + 30 // if the Y value is almost the highest, keep the tooltip under the bullets
          : (point.y as number) + layerData.margin.top
      ],
      'top'
    );
    y.current = point.y;
    x.current = point.x;
  };

  const handleMouseLeave = (_: Datum, event: MouseEvent) => {
    // To prevent hiding the tooltip when point was hovered
    if ((event.relatedTarget as any)?.id !== 'custom-point') {
      hideTooltip();
    }
  };

  return (
    <>
      {isVisible && (
        <Crosshair
          width={0}
          height={(height || DefaultChartHeight) - 120}
          type='bottom-left'
          x={x.current as number}
          y={y.current as number}
        />
      )}
      <Mesh
        nodes={[...layerData.points]}
        width={layerData.width}
        height={layerData.height}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
    </>
  );
};

export const SiteLineChart: React.FC<
  SiteComponentProps<SiteLineChartProps>
> = props => {
  const component = useSubscribeComponentChanges(SiteLineChartPlugin, props);
  const { isHover, hoverProps } = useSiteContext();
  const { nivo } = categoricalColorSchemes;
  const [activeId, setActiveId] = useState<string | number | undefined>();
  const { queryData, runQuery } = useRunQuery<LineChartQueryData[]>();
  const query = component.props?.query;
  const lineChartParams: LineChartQueryBuilderParams | undefined =
    query?.config?.pluginConfigs?.find(
      plugin => plugin.type === LineChartQueryBuilderPlugin.type
    )?.params;

  const withChartDialog = component.children?.find(
    child => child.type === 'chart-items-dialog'
  );

  const classes = ['site-line-chart'];
  if (isHover(component)) {
    classes.push('component-hover');
  }

  const isDateField = useMemo(() => {
    return {
      field: ['timestamp', 'date'].includes(
        lineChartParams?.field?.columnType || ('' as any)
      ),
      fieldLegend: ['timestamp', 'date'].includes(
        lineChartParams?.fieldLegend?.columnType || ('' as any)
      )
    };
  }, [lineChartParams]);

  const handleItemClick = (conditions: ChartItemsEventProps['conditions']) => {
    CoreEvent.dispatch<ChartItemsEventProps>(ChartItemsEvent.OpenDialog, {
      id: component.id,
      conditions
    });
  };

  const getDateRange = useCallback(
    (
      dateString: string,
      columnType?: ColumnType,
      dateFormat: DateFormat = DateFormat.Month
    ) => {
      if (
        !dateString ||
        !columnType ||
        !dateFormat ||
        !(['timestamp', 'date'] as ColumnType[]).includes(columnType)
      ) {
        return [];
      }
      const pattern = 'yyyy-MM-dd';
      const dateObject = new Date(dateString);

      switch (dateFormat) {
        case DateFormat.Day:
          const formattedDate = format(dateObject, pattern);
          return [`${formattedDate}T00:00:00`, `${formattedDate}T23:29:59`];
        case DateFormat.Month: {
          const start = format(startOfMonth(dateObject), pattern);
          const end = format(endOfMonth(dateObject), pattern);
          return [`${start}T00:00:00`, `${end}T23:29:59`];
        }
        case DateFormat.Quarter: {
          const start = format(startOfQuarter(dateObject), pattern);
          const end = format(endOfQuarter(dateObject), pattern);
          return [`${start}T00:00:00`, `${end}T23:29:59`];
        }
        case DateFormat.Year: {
          const start = format(startOfYear(dateObject), pattern);
          const end = format(endOfYear(dateObject), pattern);
          return [`${start}T00:00:00`, `${end}T23:29:59`];
        }
      }
    },
    []
  );

  const getLabel = useCallback(
    (value: any) => {
      return lineChartParams?.forWorkflowStatus && typeof value === 'string'
        ? parseTaskStatus(value)
        : value;
    },
    [lineChartParams?.forWorkflowStatus]
  );

  const customTooltip = useCallback(({ point }: PointTooltipProps) => {
    return (
      <StyledTooltip>
        <Typography>{point.serieId}</Typography>
        <div>
          <div style={{ background: point.serieColor }} className='box'></div>
          <Typography>{point.data.x}:</Typography>
          <Typography style={{ fontWeight: 500 }}>{point.data.y}</Typography>
        </div>
      </StyledTooltip>
    );
  }, []);

  const pointSymbol = useCallback(
    point => {
      return (
        <circle
          onClick={() => {
            const isNullField = point.datum.nonFormatted === 'null';
            const isNullFieldLegend = point.datum.datasetName === 'null';

            if (withChartDialog && lineChartParams?.field?.columnName) {
              const conditions: ChartItemsEventProps['conditions'] = [
                {
                  operator:
                    isDateField.field && !isNullField
                      ? 'between'
                      : isNullField
                      ? 'isNull'
                      : 'equal',
                  field: lineChartParams.field.columnName as string,
                  value:
                    isDateField.field && !isNullField
                      ? getDateRange(
                          point.datum.nonFormatted,
                          lineChartParams.field.columnType,
                          lineChartParams.field.format
                        )
                      : point.datum.nonFormatted
                }
              ];
              if (
                lineChartParams?.fieldLegend?.columnName &&
                lineChartParams?.datasetType === DatasetType.Multiple
              ) {
                conditions.push({
                  operator:
                    isDateField.fieldLegend && !isNullFieldLegend
                      ? 'between'
                      : isNullFieldLegend
                      ? 'isNull'
                      : 'equal',
                  field: lineChartParams.fieldLegend.columnName as string,
                  value:
                    isDateField.fieldLegend && !isNullFieldLegend
                      ? getDateRange(
                          point.datum.datasetName,
                          lineChartParams.fieldLegend.columnType,
                          isDateField.field
                            ? lineChartParams.field.format
                            : DateFormat.Day
                        )
                      : point.datum.datasetName
                });
              }
              conditions.length > 0 && handleItemClick(conditions);
            }
          }}
          id='custom-point'
          r='6'
          fill='#ffffff'
          stroke={point.borderColor}
          strokeWidth='2'
          style={{
            pointerEvents: 'auto',
            cursor: withChartDialog ? 'pointer' : 'auto'
          }}
        ></circle>
      );
    },
    [lineChartParams]
  );

  const lineChartData = useMemo(() => {
    if (
      !lineChartParams ||
      !queryData ||
      !lineChartParams.field?.columnName ||
      !lineChartParams.datasetType ||
      (lineChartParams.datasetType === DatasetType.Multiple &&
        !lineChartParams.fieldLegend?.columnName)
    ) {
      return [];
    }

    // format field name to match with raw data
    const [fieldLegendAlias, legendProperty] = (
      lineChartParams?.fieldLegend?.columnName || ''
    ).split('.');
    const [fieldAlias, property] = (
      lineChartParams.field.columnName || ''
    ).split('.');

    const field = fieldAlias.concat('_', snakeCase(property));
    const fieldLegend = fieldLegendAlias.concat('_', snakeCase(legendProperty));

    const keys = new Set<string>();
    const data: Array<ChartData> = [];

    const grouped = groupBy(queryData, item => item[field]);
    for (const key in grouped) {
      keys.add(key);
    }
    let orderedKeys: string[] = Array.from(keys);

    // remove the null values then put the null in the end after finalizing the completed dates below
    if (!lineChartParams.excludeNullValues) {
      orderedKeys = orderedKeys.filter(d => d != 'null');
    }

    // Sort the date or timestamp for X axis and include the dates between existing dates based on the format
    if (isDateField.field) {
      orderedKeys = orderedKeys.sort((a, b) => {
        return new Date(a).getTime() - new Date(b).getTime();
      });
      if (!!lineChartParams.field.displayAllDates) {
        const start = orderedKeys[0];
        const end = new Date(orderedKeys[orderedKeys.length - 1]);
        let current = new Date(start);
        orderedKeys = [start];

        while (current.getTime() < end.getTime()) {
          switch (lineChartParams.field?.format || DateFormat.Month) {
            case DateFormat.Day:
              current = addDays(current, 1);
              orderedKeys.push(current.toString());
              break;
            case DateFormat.Month:
              current = addMonths(current, 1);
              orderedKeys.push(current.toString());
              break;
            case DateFormat.Quarter:
              current = addQuarters(current, 1);
              orderedKeys.push(current.toString());
              break;
            default:
              current = addYears(current, 1);
              orderedKeys.push(current.toString());
              break;
          }
        }
      }
    }

    // add null if the data from api has null value
    if (!lineChartParams.excludeNullValues && keys.has('null')) {
      orderedKeys.push('null');
    }

    const groupedData =
      lineChartParams.datasetType === DatasetType.Single
        ? { [query?.config?.mainAlias?.name || 'Data Entity']: queryData }
        : groupBy(queryData, item => item[fieldLegend]);

    Object.keys(groupedData).forEach((item, index) => {
      if (item === 'undefined' || typeof item === 'undefined') {
        return;
      }
      const colorIndex =
        nivo.length === 0
          ? -1
          : index - Math.floor(index / nivo.length) * nivo.length;
      const id = getLabel(item);
      data.push({
        id,
        uniqueId: `${id}-${index}`,
        color: nivo[colorIndex],
        data: orderedKeys.map(key => {
          const fieldFormat = lineChartParams.field!.format;
          const rawData = groupedData[item].find(rd =>
            isDateField.field
              ? key === 'null'
                ? rd[field] === null
                : new Date(rd[field]).getTime() == new Date(key).getTime()
              : key === 'null'
              ? rd[field] === null
              : rd[field] == key
          );

          return {
            datasetName: id,
            nonFormatted: key,
            x: (isDateField.field &&
            typeof key !== 'undefined' &&
            key !== 'undefined' &&
            isValid(new Date(key))
              ? format(
                  new Date(key),
                  fieldFormat === DateFormat.Year
                    ? 'yyyy'
                    : fieldFormat === DateFormat.Quarter
                    ? 'QQQQ yyyy'
                    : fieldFormat === DateFormat.Day
                    ? 'MMM dd, yyyy'
                    : 'MMM yyyy'
                )
              : getLabel(key)) as string,
            y: rawData ? rawData.total : 0
          };
        })
      });
    });
    return data;
  }, [lineChartParams, queryData, query?.config?.mainAlias?.name]);

  const legendsData: ChartLegendStat[] = useMemo(() => {
    if (!lineChartParams?.showLegends || !lineChartData.length) {
      return [];
    }

    return lineChartData.map((data, i) => {
      const value = data.data.reduce(
        (prev, current) => prev + Number(current.y),
        0
      );
      return {
        id: data.uniqueId,
        value,
        tooltipText: `${data.id}: ${value}`,
        label: getLabel(data.id),
        color: data.color
      };
    });
  }, [lineChartParams, lineChartData]);

  const maxValue = useMemo(
    () => (!queryData ? 0 : Math.max(...queryData.map(data => data.total))),
    [queryData]
  );

  useEffect(() => {
    setActiveId(undefined);
  }, [
    lineChartParams?.field?.columnName,
    lineChartParams?.fieldLegend?.columnName,
    query?.config?.mainAlias?.name
  ]);

  useEffect(() => {
    if (
      (lineChartParams?.datasetType === DatasetType.Single &&
        lineChartParams?.field?.columnName) ||
      (lineChartParams?.datasetType === DatasetType.Multiple &&
        lineChartParams?.field?.columnName &&
        lineChartParams.fieldLegend?.columnName)
    ) {
      query?.config && runQuery(query.config, true);
    }
  }, [query, query?.config, lineChartParams, runQuery]);

  return (
    <SiteComponent plugin={SiteLineChartPlugin} {...props}>
      <div
        className={classes.join(' ')}
        {...hoverProps(component)}
        style={{ padding: 12 }}
      >
        <ChartContaner
          height={lineChartParams?.chartHeight}
          dataLength={lineChartData[0]?.data.length}
        >
          <ResponsiveLine
            colors={d => d.color}
            layers={[
              'grid',
              'markers',
              'axes',
              'areas',
              'lines',
              'slices',
              props => (
                <MeshAndCrosshair
                  height={lineChartParams?.chartHeight}
                  layerData={props}
                />
              ),
              'points',
              'legends'
            ]}
            pointSymbol={pointSymbol}
            curve='monotoneX'
            data={
              !!activeId
                ? [...lineChartData].filter(d => d.uniqueId === activeId)
                : lineChartData
            }
            margin={{
              top: 60,
              right: 60,
              bottom: 60,
              left: 60
            }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: maxValue,
              stacked: false,
              reverse: false
            }}
            axisBottom={
              lineChartParams?.axisTitle?.show
                ? {
                    legend: lineChartParams.axisTitle?.XTitle,
                    legendOffset: 50,
                    legendPosition: 'middle'
                  }
                : undefined
            }
            axisLeft={
              lineChartParams?.axisTitle?.show
                ? {
                    legend: lineChartParams.axisTitle?.YTitle,
                    legendOffset: -50,
                    legendPosition: 'middle'
                  }
                : undefined
            }
            pointBorderColor={{ from: 'serieColor' }}
            tooltip={customTooltip}
          />
        </ChartContaner>
        {lineChartParams?.showLegends && (
          <ChartLegends
            activeId={activeId}
            stats={legendsData}
            onItemClick={stat =>
              setActiveId(prev => (prev === stat.id ? undefined : stat.id))
            }
          />
        )}
      </div>
      {component.children?.map(child => (
        <ComponentRenderer key={child.id} component={child} />
      ))}
    </SiteComponent>
  );
};

export const SiteLineChartConfiguration: React.FC<
  SiteComponentConfigProps<SiteLineChartProps>
> = props => {
  return (
    <>
      <SiteConfigChart
        component={props.component}
        plugin={LineChartQueryBuilderPlugin}
      />
      <SiteComponentStackConfigurator component={props.component} />
    </>
  );
};

export const SiteLineChartPlugin: SiteComponentPlugin<SiteLineChartProps> = {
  name: 'Line Chart',
  type: ChartType.Line,
  configurator: SiteLineChartConfiguration,
  component: SiteLineChart,
  initConfig: component => {
    if (!component.props) component.props = {};
    // add LineChart Data set on component query config during initialization
    component.props.query = {
      config: {
        queryType: 'select',
        pluginConfigs: [{ type: LineChartQueryBuilderPlugin.type, params: {} }]
      }
    };
    return component;
  },
  slots: [
    {
      name: 'Chart Items Dialog',
      type: 'chart-items-dialog',
      slot: 'dialog'
    }
  ]
};
